<template>
  <div
    class="gallery siema"
    :class="galleryClasses"
  >
    <div
      ref="gallery"
      class="gallery__slides"
    >
      <div
        v-for="(item, index) in children"
        :key="item.id"
        ref="slides"
        class="gallery__slide-wrapper"
      >
        <span
          class="gallery__slide fancybox"
          @click.prevent="isGalleryVisible = true"
        >
          <div
            v-show="isNextVisible"
            class="siema-control siema-next"
            @click.prevent.stop="goToNextSlide"
          />
          <div
            v-show="isPrevVisible"
            class="siema-control siema-prev"
            @click.prevent.stop="goToPrevSlide"
          />

          <TheContent
            v-if="item.image"
            :key="item.image.id"
            :elem="item.image"
          />
        </span>

        <div class="siema__counter">{{ index + 1 }} / {{ totalSlides }}</div>

        <TheContent
          v-if="item.caption"
          :key="item.caption.id"
          :elem="item.caption"
        />
        <TheContent
          v-if="item.description"
          :key="item.description.id"
          :elem="item.description"
        />
      </div>
    </div>

    <FullScreenGallery
      v-if="isGalleryVisible"
      :image-urls="imagesSrc"
      :current-slide="currentSlide"
      @close="isGalleryVisible = false"
    />
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import Siema from 'siema';
  import FullScreenGallery from '~/components/content/components/FullScreenGallery.vue';
  import TheContent from '~/components/content/TheContent';
  import type { Slide } from '~/types/components/Gallery';

  const maxSlidesCount = 6;

  export default defineNuxtComponent({
    name: 'Gallery',

    components: {
      TheContent,
      FullScreenGallery,
    },

    props: {
      elem: {
        type: Object as PropType<TContent>,
        required: true,
      },
    },

    data() {
      return {
        siema: null as null | Siema,
        isGalleryVisible: false,
      };
    },

    computed: {
      children(): Slide[] {
        return this.elem.children.reduce<Slide[]>((res, item) => {
          const imageAndCaptionObject =
            item.type === 'figure' ? item : item.children.find((elem) => elem.type === 'figure');

          if (!imageAndCaptionObject) {
            return res;
          }

          const image = imageAndCaptionObject.children.find((elem) => elem.type === 'a');
          const caption = imageAndCaptionObject.children.find((elem) => elem.type === 'figcaption');
          const description = item.children.find((elem) => elem.type === 'p');

          if (!image) {
            return res;
          }

          res.push({ image, description, caption });
          return res;
        }, []);
      },

      imagesSrc(): string[] {
        return this.children.map(({ image }) =>
          image.type === 'img'
            ? image?.attributes?.src
            : (image?.children?.find((child: TContent) => child.type === 'img')?.attributes?.src ??
              ''),
        );
      },

      isNextVisible(): boolean {
        return this.siema !== null && this.siema.currentSlide < this.children.length - 1;
      },

      isPrevVisible(): boolean {
        return this.siema !== null && this.siema.currentSlide > 0;
      },

      currentSlide(): number {
        return this?.siema?.currentSlide ?? 0;
      },

      totalSlides(): number {
        if (!this.siema) {
          return 0;
        }

        const {
          siema: { innerElements },
        } = this;

        return innerElements.length;
      },

      galleryClasses(): Record<string, any> {
        return {
          'siema--numerous': this.children.length > maxSlidesCount,
        };
      },
    },

    mounted() {
      const gallery = this.$refs.gallery as HTMLElement;
      this.siema = new Siema({
        selector: gallery,
        draggable: 'ontouchstart' in document.documentElement,
        onChange: this.setGalleryHeight,
      });
    },

    methods: {
      setGalleryHeight() {
        let height = '';
        if (this.$refs.slides) {
          const index = this.siema?.currentSlide;
          // @ts-ignore
          height = this.$refs.slides[index || 0]?.clientHeight + 'px';
        }

        const gallery = this.$refs.gallery as HTMLElement;
        gallery.style.height = height;
      },

      onGalleryClose() {
        this.isGalleryVisible = false;
      },

      goToNextSlide() {
        this.siema && this.siema.next();
      },

      goToPrevSlide() {
        this.siema && this.siema.prev();
      },
    },
  });
</script>

<style lang="scss" scoped>
  $imageHeight: 240px;
  $imageHeightDesktop: 394px;

  .gallery__slides {
    overflow: visible !important;

    > div {
      &:first-child {
        display: flex;
        align-items: flex-start;

        &:after {
          content: '';
          display: table;
          clear: both;
        }
      }
    }
  }

  .gallery__slide {
    position: relative;
    // fix for https://youtrack.lifehacker.ru/issue/LH-572
    transform: translate3d(0, 0, 0);

    -webkit-tap-highlight-color: transparent;
    user-select: none;
    &--border {
      &-mobile {
        border-radius: 4px;

        img {
          padding: 12px;
          max-height: 632px !important;
          @include tablet {
            padding: 16px;
          }
        }
      }

      &-regular {
        > img {
          border-top: 1px solid #e7e7e7;
          border-bottom: 1px solid #e7e7e7;

          @include tablet {
            border-left: 1px solid #e7e7e7;
            border-right: 1px solid #e7e7e7;
          }
        }
      }
    }

    &-wrapper {
      p {
        @include fontSmallText;
        margin-top: 8px;
      }
    }
  }

  .siema {
    position: relative;
    background: transparent;
    margin-top: 24px;
    margin-left: -16px;
    margin-right: -16px;
    padding-bottom: 24px;
    overflow: hidden;

    @include tablet {
      margin-left: 0;
      margin-right: 0;
    }

    .fancybox {
      display: block;
      text-decoration: none;
      cursor: pointer;

      a {
        border-bottom: none;
      }

      :deep(img) {
        width: 100%;
        background: #eee;
        max-height: $imageHeight;
        object-fit: contain;
        object-position: center center;

        @include tablet {
          max-height: $imageHeightDesktop;
        }
      }
    }

    &__counter {
      margin-bottom: 8px;
      @include fontSmallTextBtn;
      text-align: center;
      color: #5c5c5c;
    }
  }

  .siema-caption {
    @include fontTinyText;
    padding: 0 16px;
    margin-top: 8px;

    @include tablet {
      padding: 0;
    }

    &:empty {
      display: none;
    }
  }

  .siema-description {
    @include fontSmallText;
    margin-top: 8px;
    padding: 0 16px;
    color: black;

    @include tablet {
      padding: 0;
    }

    &:empty {
      display: none;
    }
  }

  .siema-control {
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.25);
    cursor: pointer;
    transition: all 300ms;
    top: 100%;
    z-index: 3;

    @include tablet {
      top: 50%;
    }

    &:after {
      width: 8px;
      height: 8px;
      content: '';
      display: block;
      border-bottom: 2px solid #969698;
      border-left: 2px solid #969698;
      border-radius: 1px;
      position: absolute;
      top: 11px;
      transition:
        border-bottom-color 0.3s,
        border-left-color 0.3s;
    }

    @include hover {
      &:after {
        border-bottom-color: #000000;
        border-left-color: #000000;
      }
    }
  }

  .siema-prev {
    position: absolute;
    left: 14px;
    transform: translate3d(0, -50%, 0);
    width: 32px;
    height: 32px;
    border-radius: 20px;
    background: white;

    @include tablet {
      left: 16px;
    }

    &:after {
      left: 13px;
      transform: rotate(45deg);
    }
  }

  .siema-next {
    position: absolute;
    right: 14px;
    transform: translate3d(0, -50%, 0);
    width: 32px;
    height: 32px;
    border-radius: 20px;
    background: white;

    @include tablet {
      right: 16px;
    }

    &:after {
      left: 10px;
      transform: rotate(-135deg);
    }
  }
</style>
